import { useEffect, useState } from "react";
import { CircleChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../services/api/inventroy.service";
import { MarketerData } from "../components/Inventory-Stocks & Orders/Admin/InventoryManagement/types";

const AddNewMarketerModal = ({
  onClose,
  selectedMarketer,
}: {
  onClose: () => void;
  selectedMarketer: MarketerData | null;
}) => {
  const [formData, setFormData] = useState({
    marketerName: "",
    marketerAddress: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const createFormData = () => {
    const data = new FormData();
    data.append("marketerName", formData.marketerName);
    data.append("marketerAddress", formData.marketerAddress);
    if (imageFile) {
      data.append("image", imageFile);
    }
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = createFormData();
      const response = await InverntroyService.addNewMarketer(data);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
       toast.error((error as Error)?.message || "An error occurred");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!selectedMarketer) {
        toast.error("No marketer selected.");
        setIsLoading(false);
        return;
      }
      const data = createFormData();
      const response = await InverntroyService.updateMarketerDetails(
        selectedMarketer.id,
        data
      );
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error((error as Error)?.message || "An error occurred");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    if (selectedMarketer) {
      setFormData({
        marketerName: selectedMarketer.marketer_name || "",
        marketerAddress: selectedMarketer.marketer_address || "",
      });
      if (selectedMarketer.marketer_logo) {
        setImagePreview(selectedMarketer.marketer_logo);
      }
    }
  }, []);

  return (
    <div className="flex w-full">
      <div className="w-full">
        <div className="flex items-center mb-4">
          <button
            disabled={isLoading}
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full mr-2" />
          </button>
          <h2 className="font-medium text-lg text-emerald-600">
            {selectedMarketer ? "UPDATE MARKETER" : "ADD NEW MARKETER"}
          </h2>
        </div>

        <div className="border border-gray-300 bg-white shadow-lg rounded-xl mt-3 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Marketer Name
              </label>
              <input
                type="text"
                name="marketerName"
                value={formData.marketerName}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter marketer name"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Marketer Address
              </label>
              <textarea
                name="marketerAddress"
                value={formData.marketerAddress}
                onChange={handleChange as any}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter marketer address"
                rows={1}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Marketer Logo
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                {imagePreview && (
                  <div className="relative w-12 h-12 border rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-start space-x-4">
            {selectedMarketer ? (
              <button
                onClick={handleUpdate}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              >
                {isLoading ? "Updating..." : "Update Marketer"}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              >
                {isLoading ? "Adding..." : "Add Marketer"}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 w-24"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewMarketerModal;
